/**
 * The airline's four wheelchair assistance questions as Yes/No choices
 * (founder ask, 2026-10-05). Controlled: `value` is the answers object
 * (true / false / null per question), `onChange` gets the next one.
 *
 * Shared by checkout (PassengerForm, once a special request mentions a
 * wheelchair) and the trip page (WheelchairAssistanceCard), so the customer
 * answers the same questions in the same look in both places. Styled like
 * the checkout's Concierge travel questions: nothing pre-selected, because a
 * default answer is one people skim past.
 */
import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';
import {
  WHEELCHAIR_QUESTIONS,
  normalizeWheelchairAnswers,
} from '../../../supabase/functions/_shared/wheelchair-assistance.js';

const YES_NO = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const optionClass = (active, disabled) => cn(
  'relative text-left rounded-xl border px-3.5 py-2.5 min-h-[44px] transition-all duration-200',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60',
  active
    ? 'border-brand-600 bg-brand-600/15 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
    : 'border-white/10 bg-white/[0.02]',
  disabled ? 'cursor-default' : !active && 'hover:border-white/25 hover:bg-white/[0.04]',
);

function Dot({ active }) {
  return (
    <span className={cn(
      'flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full border transition-colors',
      active ? 'border-brand-300' : 'border-white/30',
    )}>
      {active && <span className="w-[7px] h-[7px] rounded-full bg-brand-300" />}
    </span>
  );
}
Dot.propTypes = { active: PropTypes.bool };

export default function WheelchairQuestions({ value, onChange, disabled = false, idPrefix = 'wheelchair' }) {
  const answers = normalizeWheelchairAnswers(value);
  return (
    <div className="space-y-4">
      {WHEELCHAIR_QUESTIONS.map((q) => (
        <div key={q.key} role="radiogroup" aria-labelledby={`${idPrefix}-${q.key}`}>
          <p id={`${idPrefix}-${q.key}`} className="text-[13.5px] font-medium text-white leading-snug">
            {q.label}
          </p>
          {q.note && (
            <p className="mt-1 text-[12px] leading-relaxed font-normal text-white/55">{q.note}</p>
          )}
          <div className="mt-2 grid grid-cols-2 gap-2.5">
            {YES_NO.map((opt) => {
              const active = answers[q.key] === opt.value;
              return (
                <button
                  key={opt.label}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled}
                  onClick={() => onChange?.({ ...answers, [q.key]: opt.value })}
                  className={optionClass(active, disabled)}
                >
                  <span className="flex items-center gap-2">
                    <Dot active={active} />
                    <span className="text-[13.5px] font-normal text-white">{opt.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

WheelchairQuestions.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  idPrefix: PropTypes.string,
};
